import { Component, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { OccurrenceEvidence } from '../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';
import { DatePicker } from '../date-picker/date-picker';

/** Uma evidência ainda por gravar: o que se escreve e os ficheiros escolhidos. */
export interface EvidenceDraft {
  evidenceDate: string;
  description: string;
  notes: string;
  files: readonly File[];
}

function isoToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function emptyEvidenceDraft(): EvidenceDraft {
  return { evidenceDate: isoToday(), description: '', notes: '', files: [] };
}

/** O tamanho da descrição de uma evidência (V8). A do seguimento pode ser maior. */
const EVIDENCE_DESCRIPTION_MAX = 500;

/**
 * A evidência anexada a uma entrada de seguimento leva a data, a descrição e as notas da própria
 * entrada (revisão de 2026-10-19): os campos repetiam-se, e quem anexava escrevia duas vezes a
 * mesma coisa. Daqui só vêm os ficheiros.
 */
export function evidenceFromFollowUp(
  draft: EvidenceDraft, entry: { entryDate: string; description: string; notes: string },
): EvidenceDraft {
  const description = entry.description.trim();
  return {
    ...draft,
    evidenceDate: entry.entryDate || isoToday(),
    description: description.length > EVIDENCE_DESCRIPTION_MAX
      ? description.slice(0, EVIDENCE_DESCRIPTION_MAX - 1) + '…'
      : description,
    notes: entry.notes,
  };
}

/**
 * Grava a evidência e envia os ficheiros que vieram com ela.
 *
 * <p>Os ficheiros pertencem a uma evidência, por isso só seguem depois de ela existir — mas
 * escolhem-se no mesmo formulário. Até 2026-10-13 só se podiam anexar depois de registar ("só
 * permite depois de registar"). Um ficheiro que falhe não desfaz a evidência: volta o nome dele,
 * para se dizer qual foi.
 */
export function saveEvidenceDraft(
  service: OccurrenceService, occurrenceId: number, draft: EvidenceDraft,
  followUpId: number | null, evidenceId: number | null = null,
): Observable<{ evidence: OccurrenceEvidence; failedFiles: string[] }> {
  const payload = {
    evidenceDate: draft.evidenceDate || null,
    description: draft.description.trim(),
    notes: draft.notes.trim() || null,
    followUpId,
  };
  const request = evidenceId === null
    ? service.addEvidence(occurrenceId, payload)
    : service.updateEvidence(occurrenceId, evidenceId, payload);
  return request.pipe(switchMap((evidence) => {
    if (!draft.files.length) return of({ evidence, failedFiles: [] as string[] });
    return forkJoin(draft.files.map((file) => service.uploadAttachment(occurrenceId, evidence.id, file).pipe(
      map(() => null as string | null),
      catchError(() => of(file.name)),
    ))).pipe(map((results) => ({
      evidence,
      failedFiles: results.filter((name): name is string => name !== null),
    })));
  }));
}

/**
 * Os campos de uma evidência — data, descrição, notas e ficheiros.
 *
 * <p>Os mesmos na secção Evidências e no registo de seguimento: "abria o campo igual ao que abre
 * na respetiva secção Evidência" (revisão de 2026-10-13).
 */
@Component({
  selector: 'app-evidence-draft',
  imports: [FormsModule, DatePicker],
  template: `
    <div class="row g-3">
      @if (!filesOnly()) {
      <div class="col-md-4">
        <label class="form-label">Data</label>
        <app-date-picker [ngModel]="draft().evidenceDate" (ngModelChange)="patch({ evidenceDate: $event })" />
      </div>
      <div class="col-md-8">
        <label class="form-label">Descrição <span class="text-danger">*</span></label>
        <!-- Área de texto: numa linha só, uma descrição longa ficava cortada à vista enquanto se
             escrevia (revisão de 2026-10-13). -->
        <textarea class="form-control" rows="2" [ngModel]="draft().description"
                  (ngModelChange)="patch({ description: $event })"
                  placeholder="O que é esta evidência"></textarea>
      </div>
      <div class="col-12">
        <label class="form-label">Notas <span class="text-muted small">(opcional)</span></label>
        <textarea class="form-control" rows="2" [ngModel]="draft().notes"
                  (ngModelChange)="patch({ notes: $event })"></textarea>
      </div>
      }
      <div class="col-12">
        <label class="form-label">
          {{ editing() ? 'Acrescentar ficheiros' : 'Ficheiros' }}
          @if (!filesOnly()) { <span class="text-muted small">(opcional)</span> }
        </label>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <label class="btn btn-sm btn-white mb-0">
            <i class="fe fe-upload me-1"></i> Escolher ficheiros
            <input type="file" multiple hidden (change)="addFiles($event)" />
          </label>
          @for (file of draft().files; track $index) {
            <span class="badge bg-light text-body border d-inline-flex align-items-center gap-1">
              <i class="fe fe-paperclip"></i> {{ file.name }}
              <button type="button" class="btn-close btn-close-sm ms-1" style="font-size: .6rem;"
                      [attr.aria-label]="'Tirar ' + file.name" (click)="removeFile($index)"></button>
            </span>
          } @empty {
            <small class="text-muted">Nenhum ficheiro escolhido.</small>
          }
        </div>
      </div>
    </div>
  `,
})
export class EvidenceDraftFields {
  readonly draft = model.required<EvidenceDraft>();
  /** Na correção os ficheiros já anexados ficam onde estão; os escolhidos aqui juntam-se. */
  readonly editing = input(false);
  /**
   * Só os ficheiros: no seguimento, a data, a descrição e as notas são as da entrada (revisão de
   * 2026-10-19).
   */
  readonly filesOnly = input(false);

  protected patch(change: Partial<EvidenceDraft>): void {
    this.draft.update((current) => ({ ...current, ...change }));
  }

  protected addFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const chosen = Array.from(input.files ?? []);
    input.value = '';
    if (chosen.length) this.patch({ files: [...this.draft().files, ...chosen] });
  }

  protected removeFile(index: number): void {
    this.patch({ files: this.draft().files.filter((_, i) => i !== index) });
  }
}
